import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CommonService } from '../common.service';
import { MatCardModule } from '@angular/material/card';
import { NgOtpInputConfig, NgOtpInputModule } from 'ng-otp-input';
import { interval, Subscription } from 'rxjs';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { ErrorDialogComponent } from '../../../portal/components/error-dialog/error-dialog.component';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-appointment-booking',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    NgOtpInputModule,
    MatIconModule,
  ],
  templateUrl: './appointment-booking.component.html',
  styleUrl: './appointment-booking.component.scss',
})
export class AppointmentBookingComponent {
  showRegistrationModal = true;
  showLoginModal = false;
  showAppointmentModal = false;
  showOtpModal = false;
  showSuccessModal = false;
  appointmentForm: FormGroup;
  otpForm: FormGroup;
  otpSent = false;
  otp!: string;
  showOtpComponent = true;
  messageCharCount = 0;
  maxMessageLength = 500;
  config: NgOtpInputConfig = {
    allowNumbersOnly: false,
    length: 6,
    isPasswordInput: false,
    disableAutoFocus: false,
    placeholder: '',
  };
  countdown: number = 60; // 1-minute timer
  isDisabled: boolean = true;
  private timerSubscription?: Subscription;
  horizontalPosition: MatSnackBarHorizontalPosition = 'end';
  verticalPosition: MatSnackBarVerticalPosition = 'bottom';

  // Store the OTP token received from send OTP API
  private otpToken: string = '';
  private verifyToken: string = '';
  isSubmitting = false;

  // Get today's date in YYYY-MM-DD format for date validation
  get today(): string {
    return new Date().toISOString().split('T')[0];
  }

  // Get current time in HH:MM format for time validation
  get currentTime(): string {
    const now = new Date();
    return now.toTimeString().slice(0, 5);
  }

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AppointmentBookingComponent>,
    private commonService: CommonService,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
  ) {
    this.appointmentForm = this.fb.group({
      fullName: [
        '',
        [Validators.required, Validators.pattern(/^[a-zA-Z\s-]+$/)],
      ],
      organizationName: [
        '',
        [Validators.required, Validators.pattern(/^[a-zA-Z\s-]+$/)],
      ],
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/,
          ),
        ],
      ],
      phoneNumber: [
        '',
        [Validators.required, Validators.pattern('^[6-9][0-9]{9}$')],
      ],
      selectTime: ['', [Validators.required, this.timeValidator.bind(this)]],
      selectDate: ['', [Validators.required, this.dateValidator.bind(this)]],
      purposeOfAppointment: [
        '',
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(500),
        ],
      ],
    });
    this.appointmentForm
      .get('purposeOfAppointment')
      ?.valueChanges.subscribe((value) => {
        this.messageCharCount = value ? value.length : 0;
      });

    this.otpForm = this.fb.group({
      otp: ['', [Validators.required]],
    });
  }

  // Custom validator for date (no past dates)
  dateValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const selectedDate = new Date(control.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare only dates

    if (selectedDate < today) {
      return { pastDate: true };
    }
    return null;
  }

  // Custom validator for time (no past time for today)
  timeValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const selectedDate = this.appointmentForm?.get('selectDate')?.value;
    if (!selectedDate) {
      return null; // Can't validate time without date
    }

    const today = new Date().toISOString().split('T')[0];
    if (selectedDate === today) {
      const currentTime = new Date().toTimeString().slice(0, 5);
      if (control.value <= currentTime) {
        return { pastTime: true };
      }
    }
    return null;
  }

  // Handle registration modal response
  onRegistrationResponse(isRegistered: boolean) {
    this.showRegistrationModal = false;
    if (isRegistered) {
      this.showLoginModal = true;
    } else {
      this.showAppointmentModal = true;
    }
  }

  // Handle login redirect
  onGoToLogin() {
    this.showLoginModal = false;
    localStorage.setItem('isAppointment', 'true');
    this.router.navigate(['/auth']);
    this.dialogRef.close();
  }

  onCloseLoginModal() {
    this.showLoginModal = false;
    this.dialogRef.close();
  }
  onSubmitAppointment() {
    if (this.appointmentForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;

      const phoneNumber = this.appointmentForm.get('phoneNumber')?.value;
      const sendOtpPayload = {
        phone: phoneNumber,
      };
      this.commonService.sendOtpForm(sendOtpPayload).subscribe({
        next: (response) => {
          this.startTimer();
          if (response.body && response.body.token) {
            this.otpToken = response.body.token;
            this.openSnackBar(
              'OTP has been successfully sent. Please check your messages.',
              'Closed',
            );
          }
          this.showAppointmentModal = false;
          this.showOtpModal = true;
          this.otpSent = true;
          this.isSubmitting = false;
        },
        error: (error) => {
          this.isSubmitting = false;
          this.errorDialog(error.error.message, 'Invalid action');
        },
      });
    } else {
      this.markFormGroupTouched(this.appointmentForm);
    }
  }
  onDateChange() {
    const timeControl = this.appointmentForm.get('selectTime');
    if (timeControl) {
      timeControl.updateValueAndValidity();
    }
  }
  onVerifyOtp() {
    const otpValue = this.otp;
    if (otpValue.length === 6 && this.otpToken && !this.isSubmitting) {
      this.isSubmitting = true;

      const verifyOtpPayload = {
        otp: otpValue,
        token: this.otpToken,
      };

      // Call verify OTP API
      this.commonService.verifyOtpForm(verifyOtpPayload).subscribe({
        next: (response) => {
          if (response.body && response.body.token) {
            this.verifyToken = response.body.token;
          }
          this.submitAppointmentData();
        },
        error: (error) => {
          this.isSubmitting = false;
          if (error.status === 400) {
            this.errorDialog('Re-enter correct OTP', 'Invalid OTP');
          } else if (error.error && error.error.message) {
            this.errorDialog(error.error.message, 'Invalid action');
          } else {
            this.errorDialog(
              'Something went wrong. Please try again.',
              'Error',
            );
          }
        },
      });
    } else {
      alert('Please enter a valid 6-digit OTP');
    }
  }
  // Step 3: Submit appointment data after OTP verification
  private submitAppointmentData() {
    const isoDate = this.appointmentForm.get('selectDate')?.value;
    let formattedDate = '';
    if (isoDate) {
      const date = new Date(isoDate);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      formattedDate = `${day}/${month}/${year}`;
    }

    const appointmentPayload = {
      fullName: this.appointmentForm.get('fullName')?.value,
      organizationName: this.appointmentForm.get('organizationName')?.value,
      email: this.appointmentForm.get('email')?.value,
      mobileNumber: this.appointmentForm.get('phoneNumber')?.value,
      selectDate: formattedDate,
      selectTime: this.appointmentForm.get('selectTime')?.value,
      description: this.appointmentForm.get('purposeOfAppointment')?.value,
    };

    this.commonService
      .appointmentForm(appointmentPayload, this.verifyToken)
      .subscribe({
        next: (response) => {
          if (response.body) {
            this.showOtpModal = false;
            this.showSuccessModal = true;
            this.isSubmitting = false;
          }
        },
        error: (error) => {
          this.errorDialog(
            error.error?.message ||
              'Failed to submit appointment. Please try again.',
            'Error',
          );
          this.isSubmitting = false;
        },
      });
  }
  // Step 3: resend OTP
  onResendOtp() {
    if (this.isDisabled) return;

    this.isDisabled = true;
    this.countdown = 60;
    this.startTimer();

    this.otp = '';
    this.showOtpComponent = false;
    setTimeout(() => {
      this.showOtpComponent = true;
    }, 100);
    const phoneNumber = this.appointmentForm.get('phoneNumber')?.value;
    const sendOtpPayload = {
      phone: phoneNumber,
    };

    this.commonService.sendOtpForm(sendOtpPayload).subscribe({
      next: (response) => {
        if (response.body && response.body.token) {
          this.otpToken = response.body.token;
        }

        this.openSnackBar(
          'OTP has been resent to your phone number.',
          'Closed',
        );
      },
      error: (error) => {
        this.errorDialog(
          error.error?.message || 'Failed to resend OTP. Please try again.',
          'Error',
        );
      },
    });
  }
  onOtpChange(otp: string) {
    this.otp = otp;
  }
  private startTimer() {
    this.timerSubscription = interval(1000).subscribe(() => {
      if (this.countdown > 0) {
        this.countdown--;
      } else {
        this.isDisabled = false;
        this.timerSubscription?.unsubscribe();
      }
    });
  }

  onCloseSuccessModal() {
    this.showSuccessModal = false;
    this.appointmentForm.reset();
    this.otpForm.reset();
    this.otpToken = '';
    this.otpSent = false;
    this.dialogRef.close('save');
  }

  closeModal() {
    this.dialogRef.close();
  }
  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }
  hasError(controlName: string): boolean {
    const control = this.appointmentForm.get(controlName);
    return !!(control && control.invalid && control.touched);
  }

  getErrorMessage(controlName: string): string {
    const control = this.appointmentForm.get(controlName);
    if (!control || !control.errors || !control.touched) {
      return '';
    }

    const errors = control.errors;

    switch (controlName) {
      case 'fullName':
        if (errors['required']) return 'Full name is required';
        if (errors['pattern'])
          return 'Invalid format. Only letters, spaces, & hyphens allowed';
        break;

      case 'organizationName':
        if (errors['required']) return 'Organization name is required';
        if (errors['pattern'])
          return 'Invalid format. Only letters, spaces, & hyphens allowed';
        break;

      case 'email':
        if (errors['required']) return 'Email is required';
        if (errors['pattern']) return 'Please enter a valid email address';
        break;

      case 'phoneNumber':
        if (errors['required']) return 'Phone number is required';
        if (errors['pattern'])
          return 'Please enter a valid 10-digit phone number';
        break;

      case 'selectDate':
        if (errors['required']) return 'Date is required';
        if (errors['pastDate']) return 'Please select a future date';
        break;

      case 'selectTime':
        if (errors['required']) return 'Time is required';
        if (errors['pastTime']) return 'Please select a future time for today';
        break;

      case 'purposeOfAppointment':
        if (errors['required']) return 'Purpose of appointment is required';
        break;
    }

    return '';
  }
  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 9000,
      horizontalPosition: this.horizontalPosition,
      verticalPosition: this.verticalPosition,
    });
  }

  errorDialog(message: string, statusText: string): void {
    const dialogRef = this.dialog.open(ErrorDialogComponent, {
      width: '400px',
      data: {
        message,
        statusText,
      },
    });
    dialogRef.afterClosed();
  }
}
